import {
    assigneePanelKeyFromType,
    FALLBACK_ASSIGNEE_TYPES,
    findTeamMemberByUserId,
    formatMinutesLabel,
    normalizeActionPlanTeamList,
    normalizeTeamGroupList,
    normalizeTemplateUsersList,
    normalizeVendorCompanyList,
    normalizeVendorContactList,
    resolveAssigneeTypes,
} from 'page-components/auto-ticket-rules/utils'
import { getAssigneeTypeLabel } from 'page-components/auto-ticket/utils'

export const getApiErrorMessage = err => {
    const data = err?.error ?? err?.response?.data
    if (typeof data === 'string' && data.trim()) return data
    const nested = data?.error
    const errors = data?.errors || data?.Errors || data?.ModelState || nested?.errors
    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        const parts = Object.keys(errors)
            .map(key => {
                const val = errors[key]
                if (Array.isArray(val)) return val.filter(Boolean).join(' ')
                if (typeof val === 'string') return val
                return ''
            })
            .filter(Boolean)
        if (parts.length) return parts.join(' ')
    }
    if (Array.isArray(data?.Errors) && data.Errors.length) {
        return data.Errors.map(e => e?.Message || e?.message || e).join(' ')
    }
    return (
        data?.Message ??
        data?.message ??
        data?.title ??
        data?.ErrorMessage ??
        nested?.Message ??
        nested?.message ??
        err?.message ??
        'Something went wrong'
    )
}

const WATCHER_TYPE = {
    user: 1,
    team: 2,
    vendorCompany: 3,
    vendorContact: 4,
    /** External email CC — Postman Escalation V1 */
    externalEmail: 9,
}

/** POST /ActionPlan/Ticket/{id}/Watcher — ActionPlanTicketWatcherCreateDto
 *  Swagger fields: TicketId, WatcherTypeId, WatcherId, WatcherEmail, WatcherPhone, Notify*
 *  External email (Postman #10): WatcherTypeId=9 + WatcherEmail.
 *  Prod treats null WatcherId as "missing recipient" — send 0 for email CCs.
 */
export const buildWatcherPayload = ({
    ticketId,
    mode,
    typeId,
    recipientId,
    watcherEmail,
    watcherPhone,
    notifyOnCreated,
    notifyOnUpdated,
    notifyOnEscalated,
    notifyOnResolved,
}) => {
    const ticket = Number(ticketId)
    const payload = {
        NotifyOnCreated: Boolean(notifyOnCreated),
        NotifyOnUpdated: Boolean(notifyOnUpdated),
        NotifyOnEscalated: Boolean(notifyOnEscalated),
        NotifyOnResolved: Boolean(notifyOnResolved),
    }
    if (Number.isFinite(ticket) && ticket > 0) {
        payload.TicketId = ticket
    }

    const email = String(watcherEmail || '').trim()
    const phone = String(watcherPhone || '').trim()
    if (phone) payload.WatcherPhone = phone

    if (mode === 'email') {
        if (!email) {
            throw new Error('External email is required.')
        }
        payload.WatcherTypeId = WATCHER_TYPE.externalEmail
        payload.WatcherEmail = email
        // Avoid null WatcherId — backend returns "missing recipient or DB error"
        payload.WatcherId = 0
        return payload
    }

    const watcherTypeId = Number(typeId) || WATCHER_TYPE.user
    const watcherId = Number(recipientId)
    if (!Number.isFinite(watcherId) || watcherId <= 0) {
        throw new Error(
            'Select a valid user, team, or vendor (numeric recipient id required).',
        )
    }
    if (![1, 2, 3, 4].includes(watcherTypeId)) {
        throw new Error('Invalid watcher type.')
    }

    payload.WatcherTypeId = watcherTypeId
    payload.WatcherId = watcherId
    if (email) payload.WatcherEmail = email
    return payload
}

export const CREATE_NEW_POLICY_KEY = '__create_new_policy__'

export const extractList = response => {
    if (Array.isArray(response)) return response
    if (Array.isArray(response?.Items)) return response.Items
    if (Array.isArray(response?.Data)) return response.Data
    if (Array.isArray(response?.data)) return response.data
    if (Array.isArray(response?.Records)) return response.Records
    if (Array.isArray(response?.Policies)) return response.Policies
    return []
}

export const normalizeEscalationPolicy = policy => {
    const reminder = policy?.ReminderMinutes ?? policy?.reminderMinutes
    const reminderNum = Number(reminder)
    return {
        id:
            policy?.EscalationPolicyId ??
            policy?.PolicyId ??
            policy?.Id ??
            policy?.id,
        name: String(policy?.Name ?? policy?.name ?? '').trim(),
        reminderMinutes:
            reminder == null || reminder === '' || !Number.isFinite(reminderNum)
                ? null
                : reminderNum,
        recipientTypeId: Number(
            policy?.EscalationRecipientTypeId ??
                policy?.escalationRecipientTypeId ??
                1,
        ),
        recipientId:
            policy?.EscalationRecipientId ??
            policy?.escalationRecipientId ??
            null,
        recipientName: String(
            policy?.EscalationRecipientName ??
                policy?.RecipientName ??
                policy?.recipientName ??
                '',
        ).trim(),
        recipientTypeName: String(
            policy?.EscalationRecipientTypeName ??
                policy?.recipientTypeName ??
                '',
        ).trim(),
        vendorCompanyId:
            policy?.VendorCompanyId ?? policy?.vendorCompanyId ?? null,
        isActive: policy?.IsActive !== false && policy?.isActive !== false,
        rulesUsingThis: Number(
            policy?.RulesUsingThis ??
                policy?.RuleCount ??
                policy?.LinkedRuleCount ??
                policy?.rulesUsingThis ??
                0,
        ),
    }
}

export const normalizeEscalationPolicyList = response =>
    extractList(response)
        .map(normalizeEscalationPolicy)
        .filter(p => p.id != null && p.id !== '')

export const buildPolicyPayload = form => {
    const reminderOn = Boolean(form.reminderEnabled)
    const reminderMinutes = Number(form.reminderMinutes)
    return {
        Name: String(form.name || '').trim(),
        ReminderMinutes:
            reminderOn && Number.isFinite(reminderMinutes) && reminderMinutes > 0
                ? reminderMinutes
                : null,
        EscalationRecipientTypeId: Number(form.recipientTypeId) || 1,
        EscalationRecipientId: Number(form.recipientId),
        IsActive: form.isActive !== false,
    }
}

export const formatReminderLabel = minutes => {
    if (minutes == null || minutes === '' || Number(minutes) <= 0) {
        return '— (no soft reminder)'
    }
    return formatMinutesLabel(minutes)
}

export const formatRecipientChipLabel = (typeId, typeName) => {
    if (typeName) {
        const n = String(typeName).toLowerCase()
        if (n.includes('vendor')) return 'Vendor'
        if (n.includes('team')) return 'Team'
        if (n.includes('user')) return 'User'
        return typeName
    }
    return getAssigneeTypeLabel(typeId)
}

export const formatPolicyDropdownLabel = policy => {
    if (!policy) return ''
    const reminderBit = policy.reminderMinutes
        ? `${policy.reminderMinutes} min reminder`
        : 'no reminder'
    const toBit = policy.recipientName || 'recipient'
    return `${policy.name} (${reminderBit} → ${toBit})`
}

export const formatPolicyHint = policy => {
    if (!policy) return ''
    const reminderBit = policy.reminderMinutes
        ? `Reminder at ${policy.reminderMinutes} min`
        : 'No soft reminder'
    const toBit = policy.recipientName
        ? `Escalates to ${policy.recipientName}`
        : 'Escalation recipient set'
    return `Currently selected: ${policy.name} · ${reminderBit} · ${toBit}.`
}

export const policyMatchesSearch = (policy, search) => {
    const q = String(search || '')
        .trim()
        .toLowerCase()
    if (!q) return true
    return (
        policy.name.toLowerCase().includes(q) ||
        policy.recipientName.toLowerCase().includes(q) ||
        String(policy.id).includes(q)
    )
}

export const lookupRecipientDisplayName = (
    typeId,
    recipientId,
    { teamMembers = [], teams = [], vendors = [], contacts = [] } = {},
) => {
    if (recipientId == null || recipientId === '') return ''
    const tid = Number(typeId)
    if (tid === 1) {
        const member = findTeamMemberByUserId(teamMembers, recipientId)
        if (!member) return ''
        return String(
            member.FullName ||
                member.Name ||
                `${member.FirstName || ''} ${member.LastName || ''}`.trim() ||
                member.Email ||
                '',
        ).trim()
    }
    let list = []
    if (tid === 2) list = teams
    else if (tid === 3) list = vendors
    else if (tid === 4) list = contacts
    const row = (list || []).find(
        item => Number(item.id) === Number(recipientId),
    )
    return String(row?.name || '').trim()
}

export const enrichPoliciesWithRecipientNames = (policies, lookups) =>
    (policies || []).map(policy => {
        if (policy.recipientName) return policy
        const resolved = lookupRecipientDisplayName(
            policy.recipientTypeId,
            policy.recipientId,
            lookups,
        )
        if (resolved) return { ...policy, recipientName: resolved }
        if (policy.recipientId != null && policy.recipientId !== '') {
            const kind = formatRecipientChipLabel(
                policy.recipientTypeId,
                policy.recipientTypeName,
            )
            return {
                ...policy,
                recipientName: `${kind} #${policy.recipientId}`,
            }
        }
        return policy
    })

export const mapTeamMemberToPickerUser = member => {
    // ActionPlan WatcherId / EscalationRecipientId need a numeric id.
    // TeamMember rows may have UserId as a non-numeric string and unique_id as
    // the real int — prefer the first finite number in this order.
    const candidates = [
        member?.UserId,
        member?.userId,
        member?.unique_id,
        member?.UniqueId,
        member?.Id,
        member?.id,
    ].filter(v => v != null && v !== '')

    let id = null
    candidates.forEach(candidate => {
        if (id != null) return
        const numeric = Number(candidate)
        if (Number.isFinite(numeric) && numeric !== 0) {
            id = numeric
        }
    })

    return {
        id,
        ids: [...new Set(candidates.map(String))],
        name: String(
            member?.FullName ??
                member?.Name ??
                `${member?.FirstName || ''} ${member?.LastName || ''}`.trim(),
        ).trim(),
        email: member?.Email ?? member?.email ?? '',
        role: member?.RoleName ?? member?.JobTitle ?? '',
    }
}

export const pickerItemMatchesId = (item, recipientId) => {
    if (item == null || recipientId == null || recipientId === '') return false
    const target = String(recipientId)
    const candidates = [item.id, ...(item.ids || [])]
        .filter(v => v != null && v !== '')
        .map(String)
    return candidates.includes(target)
}

export const normalizePickerUsers = response => {
    const fromTemplate = normalizeTemplateUsersList(response)
    if (fromTemplate.length) return fromTemplate
    const list = extractList(response)
    return list
        .map(mapTeamMemberToPickerUser)
        .filter(u => u.id != null && u.id !== '')
}

export {
    assigneePanelKeyFromType,
    FALLBACK_ASSIGNEE_TYPES,
    normalizeActionPlanTeamList,
    normalizeTeamGroupList,
    normalizeVendorCompanyList,
    normalizeVendorContactList,
    resolveAssigneeTypes,
}

export const normalizeWatcher = item => ({
    watcherId:
        item?.TicketWatcherId ??
        item?.WatcherId ??
        item?.Id ??
        item?.id,
    name: String(
        item?.FullName ??
            item?.Name ??
            item?.WatcherName ??
            item?.Email ??
            item?.WatcherEmail ??
            item?.ExternalEmail ??
            '',
    ).trim(),
    email: String(
        item?.WatcherEmail ??
            item?.Email ??
            item?.ExternalEmail ??
            item?.email ??
            '',
    ).trim(),
    watcherTypeId: Number(
        item?.WatcherTypeId ??
            item?.AssignedToTypeId ??
            item?.watcherTypeId ??
            1,
    ),
    recipientId: item?.WatcherId ?? item?.AssignedToId ?? item?.recipientId,
    addedOn: item?.CreatedOn ?? item?.AddedOn ?? item?.createdOn,
    addedByPolicy: Boolean(
        item?.AddedByPolicy ?? item?.IsPolicyWatcher ?? item?.addedByPolicy,
    ),
    notifyOnCreated: Boolean(item?.NotifyOnCreated ?? item?.notifyOnCreated),
    notifyOnUpdated: Boolean(item?.NotifyOnUpdated ?? item?.notifyOnUpdated),
    notifyOnEscalated: Boolean(
        item?.NotifyOnEscalated ?? item?.notifyOnEscalated ?? true,
    ),
    notifyOnResolved: Boolean(item?.NotifyOnResolved ?? item?.notifyOnResolved),
    isExternal: Boolean(
        item?.IsExternal ||
            Number(item?.WatcherTypeId ?? item?.watcherTypeId) ===
                WATCHER_TYPE.externalEmail ||
            ((item?.WatcherEmail || item?.ExternalEmail) &&
                !item?.WatcherId),
    ),
})

export const normalizeWatcherList = response =>
    extractList(response)
        .map(normalizeWatcher)
        .filter(w => w.watcherId != null && w.watcherId !== '')

export const getWatcherInitials = watcher => {
    if (watcher?.isExternal) return '@'
    const cleaned = String(watcher?.name || watcher?.email || '')
        .trim()
    if (!cleaned) return '?'
    const parts = cleaned.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return parts[0].slice(0, 2).toUpperCase()
}

export const formatWatcherMeta = watcher => {
    let type = getAssigneeTypeLabel(watcher?.watcherTypeId)
    if (watcher?.isExternal) {
        type = 'External email'
    } else if (type === 'User') {
        type = 'Internal user'
    }
    if (watcher?.addedByPolicy) {
        return `${type} · added by policy`
    }
    return type
}
